import React, { useCallback, useMemo, useRef } from 'react';
import { defaultProps } from '@jira/platform__default-props/src/defaultProps.tsx';
import { token } from '@atlaskit/tokens';
import { MAX_LABEL_LENGTH, STATE_COLOURS, TRANSITION_STROKE_WIDTH } from './ellipsis.tsx';
import { EDGE_TYPE } from './directionKeys.tsx';
import {
	type Point,
	type TransitionIcons,
	ValidationFlag,
	toPoint,
	type ConnectionPorts,
} from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import { areConnectionsEqual, truncateLabel } from './isEveryPropertyDefined.tsx';
import { DATA_ATTRIBUTE_IGNORE_AUTOFIT } from './getVerticalOffset.tsx';
import { useLayoutWidth } from './useLayoutWidth.tsx';
import { useStrokeColor } from './useStrokeColor.tsx';

import { IconSet } from './IconSet.tsx';

export interface Props {
	id: string;
	label?: string;
	icons?: TransitionIcons[];
	flag?: ValidationFlag;
	isHovered?: boolean;
	isHighlighted?: boolean;
	isImmutable?: boolean;
	isSelected?: boolean;
	isTargeted?: boolean;
	isOverlapping?: boolean;
	isOverlapHovered?: boolean;
	collapseTransitionLabels?: boolean;
	position?: Point | undefined;
	maxLabelLength?: number;
	connection?: ConnectionPorts;
	hoveredItem?: ItemReference;
	onSelect?: (item: ItemReference | undefined) => void;
	onHover?: (item: ItemReference | undefined) => void;
}

const TEXT_Y_OFFSET = 16;
const ICON_LABEL_GAP = 2;
const ICON_PADDING = 2;
const ICON_ONLY_LABEL_PADDING = 16;
const LABEL_PADDING = 20;
const TEXT_X_IF_NO_ICONS = 20;
const LABEL_HEIGHT = 24;
const TEXT_OFFSET = 6;
const ERROR_RING_DASH_ARRAY = '3,2';
const FOCUS_RING_WIDTH = 4;

const TransitionLabel = ({
	id,
	label = '',
	flag,
	icons = defaultProps.emptyArray,
	isHovered = false,
	isHighlighted = false,
	isImmutable = false,
	isSelected = false,
	isTargeted = false,
	position,
	maxLabelLength = MAX_LABEL_LENGTH,
	onSelect,
	onHover,
	collapseTransitionLabels = false,
	connection,
	hoveredItem,
}: Props): React.JSX.Element | null => {
	const textRef = useRef<SVGTextElement>(null);
	const iconGroupRef = useRef<SVGGElement>(null);
	const textWidth = useLayoutWidth(textRef, [label, icons, position]);
	const iconsWidth = useLayoutWidth(iconGroupRef, [label, icons, position]);
	const labelWidth =
		label === ''
			? textWidth + iconsWidth + ICON_ONLY_LABEL_PADDING
			: textWidth + iconsWidth + LABEL_PADDING;
	const textTransformX = iconsWidth ? TEXT_X_IF_NO_ICONS : (labelWidth - textWidth) / 2;
	const textX = iconsWidth && iconsWidth - TEXT_OFFSET;

	const stroke = useStrokeColor({
		flag,
		isHighlighted,
		isHovered,
		isSelected,
	});

	const onGroupClick = useCallback(() => {
		if (!isImmutable) {
			onSelect?.({ type: EDGE_TYPE, id });
		}
	}, [isImmutable, onSelect, id]);

	const onMouseEnter = useCallback(() => {
		onHover?.({ id, type: EDGE_TYPE, connection });
	}, [id, onHover, connection]);

	const onMouseLeave = useCallback(() => {
		onHover?.(undefined);
	}, [onHover]);

	const isVisible = useMemo(() => {
		if (!collapseTransitionLabels || isSelected || isTargeted) return true;

		const isConnectionHovered =
			hoveredItem?.type === EDGE_TYPE && areConnectionsEqual(connection, hoveredItem?.connection);

		return isConnectionHovered;
	}, [collapseTransitionLabels, isSelected, isTargeted, hoveredItem, connection]);

	if (icons.length === 0 && label === '') {
		return null;
	}

	const truncatedLabel = truncateLabel(label, maxLabelLength);

	if (!position) {
		return null;
	}

	const labelX = position.x - labelWidth / 2;
	const labelY = position.y - LABEL_HEIGHT / 2;

	const hasLabelText = label !== '';
	const hasError = flag === ValidationFlag.Error;
	const hasWarning = flag === ValidationFlag.Warn;
	const hasValidationFlag = hasError || hasWarning;

	if (!isVisible) {
		return null;
	}

	return (
		<g
			data-testid="accessible-workflow-diagram.transition-label"
			onClick={onGroupClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			// See HIX-3948 - There is a bug in Safari (last tested on v17.1) where getBBox for this element
			// is always { x: 0, y: 0 } even after the element has been rendered, unless there is label text.
			// To mitigate this we're using this attribute to filter this element out of calculations.
			{...(!label && DATA_ATTRIBUTE_IGNORE_AUTOFIT)}
		>
			{/* Target ring */}
			{hasLabelText && isTargeted && (
				<rect
					x={labelX}
					y={labelY}
					width={labelWidth}
					height={LABEL_HEIGHT}
					rx={LABEL_HEIGHT / 2}
					ry={LABEL_HEIGHT / 2}
					stroke={STATE_COLOURS.focused}
					strokeWidth={FOCUS_RING_WIDTH * 2}
				/>
			)}
			{/* Label background */}
			<rect
				x={labelX}
				y={labelY}
				width={labelWidth}
				height={LABEL_HEIGHT}
				rx={LABEL_HEIGHT / 2}
				ry={LABEL_HEIGHT / 2}
				fill={token('color.background.input')}
				stroke={stroke}
				strokeWidth={TRANSITION_STROKE_WIDTH}
				strokeDasharray={!isSelected && hasValidationFlag ? ERROR_RING_DASH_ARRAY : undefined}
			/>
			{/* Icons */}
			<g ref={iconGroupRef}>
				<IconSet
					icons={icons}
					anchor={toPoint(labelX + ICON_LABEL_GAP + ICON_PADDING, labelY + ICON_PADDING)}
				/>
			</g>
			{/* Transition label text */}
			<text
				ref={textRef}
				x={textX + labelX + textTransformX}
				y={labelY + TEXT_Y_OFFSET}
				textAnchor="start"
				fontSize="12px"
				fill={token('color.text')}
			>
				{truncatedLabel}
			</text>
		</g>
	);
};

export default TransitionLabel;
